<script setup lang="ts">
import { computed } from "vue";
import type { ButtonTone } from "@/components/shared/buttons/types";
import { Z_INDEX } from "@/constants/zIndex";

// Activator bindings (`v-bind="activatorProps"`) must reach the inner button, not the
// tippy wrapper, or the menu would open from the wrong element.
defineOptions({ inheritAttrs: false });

type ButtonVariant = "flat" | "text" | "elevated" | "tonal" | "outlined" | "plain";
type ButtonType = "button" | "submit" | "reset";
type ButtonKind = "primary" | "secondary";
type ClassValue = string | Record<string, boolean> | ClassValue[];

/**
 * v-btn wrapper that can show a tippy tooltip while disabled.
 *
 * Native `disabled` uses `pointer-events: none`, so tippy never fires. When
 * `disabledTooltip` is set, we fake-disable (aria + class) and keep pointer
 * events so the reason can be shown on hover. Tooltips inside dialogs need
 * `strategy: 'fixed'` and a z-index above Vuetify overlays.
 *
 * Works as a `v-menu`/`v-tooltip` activator: `inheritAttrs: false` puts the
 * activator's bound listeners and aria attrs on the inner `v-btn` rather than the
 * tippy wrapper, and `onClick` still swallows the click while disabled so the
 * menu cannot open.
 */
const props = withDefaults(
  defineProps<{
    disabled?: boolean;
    loading?: boolean;
    type?: ButtonType;

    /** Tooltip while the button is enabled. Usually omitted. */
    tooltip?: string;
    /** Tooltip while disabled — explains why the action is unavailable. */
    disabledTooltip?: string;
    /** Force-hide any tooltip (enabled or disabled). */
    disableTooltip?: boolean;

    tone?: ButtonTone;
    kind?: ButtonKind;
    color?: string;
    variant?: ButtonVariant;
    size?: string | number;
    rounded?: string | number | boolean;
    prependIcon?: string;
    appendIcon?: string;
    /** Icon-only button (e.g. `mdi-dots-vertical`); renders without a label slot. */
    icon?: string;
    block?: boolean;
    class?: ClassValue;

    /** Tippy z-index. Defaults to the shared tooltip layer, above every dialog overlay. */
    tippyZIndex?: number;
  }>(),
  {
    disabled: false,
    loading: false,
    type: "button",
    disableTooltip: false,
    kind: "primary",
    variant: "flat",
    size: "large",
    rounded: "lg",
    tippyZIndex: Z_INDEX.tooltip,
  }
);

const emit = defineEmits<{
  click: [event: MouseEvent];
}>();

const isDisabled = computed(() => props.disabled || props.loading);

const activeTooltip = computed(() => {
  if (props.disableTooltip) return undefined;
  if (isDisabled.value) return props.disabledTooltip || undefined;
  return props.tooltip || undefined;
});

/** Native disabled only when there is nothing to show on hover. */
const nativeDisabled = computed(() => isDisabled.value && !activeTooltip.value);

/**
 * Visually disabled but still hoverable so tippy can show `disabledTooltip`.
 * Clicks are still blocked in `onClick`.
 */
const fakeDisabled = computed(() => isDisabled.value && !!activeTooltip.value);

const tippyOptions = computed(() => {
  if (!activeTooltip.value) return undefined;
  return {
    content: activeTooltip.value,
    zIndex: props.tippyZIndex,
    popperOptions: { strategy: "fixed" as const },
  };
});

const useOrbisStyle = computed(() => props.tone !== undefined);

const toneClass = computed(() => {
  if (!useOrbisStyle.value) return "";
  const base = props.kind === "secondary" ? "tooltip-button--secondary" : "tooltip-button--primary";
  return props.tone === "danger" ? `${base} ${base}-danger` : base;
});

const buttonClass = computed(() => [
  props.class,
  useOrbisStyle.value ? ["orbis-btn", toneClass.value] : "",
  fakeDisabled.value ? "v-btn--disabled tw:!pointer-events-auto tooltip-button--fake-disabled" : "",
]);

/**
 * Vuetify renders `color` as an inline style, which outranks any disabled-state rule in the
 * stylesheet. Dropping it while fake-disabled is what actually greys the button out.
 */
const resolvedColor = computed(() => {
  if (useOrbisStyle.value || fakeDisabled.value || !props.color) return undefined;
  return props.color;
});

const onClick = (event: MouseEvent) => {
  if (isDisabled.value) {
    event.preventDefault();
    event.stopPropagation();
    return;
  }
  emit("click", event);
};
</script>

<template>
  <div
    v-tippy="tippyOptions"
    class="tw:inline-flex"
    :class="{ 'tw:w-full': block }"
  >
    <v-btn
      v-bind="$attrs"
      :type="type"
      :class="buttonClass"
      :color="resolvedColor"
      :variant="useOrbisStyle ? 'flat' : variant"
      :size="size"
      :rounded="useOrbisStyle ? undefined : rounded"
      :prepend-icon="prependIcon || undefined"
      :append-icon="appendIcon || undefined"
      :icon="icon || undefined"
      :block="block"
      :loading="loading"
      :disabled="nativeDisabled"
      :aria-disabled="isDisabled || undefined"
      :tabindex="fakeDisabled ? -1 : undefined"
      @click="onClick"
    >
      <v-icon v-if="icon">{{ icon }}</v-icon>
      <slot v-else />
    </v-btn>
  </div>
</template>

<style scoped>
:deep(.tooltip-button--fake-disabled) {
  opacity: 0.55;
}

.tooltip-button--primary {
  padding: 0 var(--btn-px, 26px) !important;

  border-radius: var(--btn-radius) !important;

  background: linear-gradient(180deg, var(--btn-brand-top), var(--btn-brand-bottom)) !important;
  color: var(--btn-on-brand) !important;

  font-size: var(--btn-fs);
  font-weight: 640;
  letter-spacing: -0.006em;

  box-shadow: var(--btn-shadow-brand);
}

.tooltip-button--primary:hover:not(:disabled):not(.v-btn--disabled) {
  box-shadow: var(--btn-shadow-brand-hover);
}

.tooltip-button--primary-danger {
  background: linear-gradient(180deg, var(--btn-danger-top), var(--btn-danger-bottom)) !important;
  color: var(--btn-on-danger) !important;

  box-shadow: var(--btn-shadow-danger);
}

.tooltip-button--primary-danger:hover:not(:disabled):not(.v-btn--disabled) {
  box-shadow: var(--btn-shadow-danger-hover);
}

.tooltip-button--secondary {
  padding: 0 var(--btn-px, 22px) !important;

  border: 1px solid var(--btn-hairline) !important;
  border-radius: var(--btn-radius) !important;

  background: var(--btn-surface) !important;
  color: var(--btn-ink-muted) !important;

  font-size: var(--btn-fs);
  font-weight: 600;

  box-shadow: var(--btn-shadow);
}

.tooltip-button--secondary:hover:not(:disabled):not(.v-btn--disabled) {
  color: var(--btn-ink) !important;
  border-color: var(--btn-hairline-strong) !important;
  background: var(--btn-surface-hover) !important;

  box-shadow: var(--btn-shadow-hover);
}

.tooltip-button--secondary-danger {
  border-color: rgba(var(--v-theme-error), 0.4) !important;
  color: var(--btn-danger) !important;
}

.tooltip-button--secondary-danger:hover:not(:disabled):not(.v-btn--disabled) {
  color: var(--btn-danger) !important;
  border-color: var(--btn-danger) !important;
  background: var(--btn-danger-surface-hover) !important;
}
</style>
